import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, CheckCircle2, MinusCircle, XCircle, SpellCheck } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const NIVEL_META = {
    cumple: { icon: CheckCircle2, style: "text-emerald-700", label: "Cumple" },
    parcial: { icon: MinusCircle, style: "text-amber-700", label: "Parcial" },
    no_cumple: { icon: XCircle, style: "text-red-700", label: "No cumple" },
};

export default function EvaluacionOralReporte() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/oral-assessments/sessions/${sessionId}`)
            .then((r) => setSession(r.data))
            .catch((e) => toast.error(e.response?.data?.detail || "Error cargando la sesión"))
            .finally(() => setLoading(false));
    }, [sessionId]);

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!session) return <div className="p-10 text-edu-muted">No se pudo cargar.</div>;

    const report = session.report;
    const grammar = session.grammar_report;
    const errorsByRound = {};
    ((grammar && grammar.errors) || []).forEach((e) => {
        const k = e.ronda;
        (errorsByRound[k] = errorsByRound[k] || []).push(e);
    });

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="edu-card p-6 mb-4">
                <h1 className="text-xl font-semibold mb-1">{session.titulo}</h1>
                <p className="text-xs text-edu-muted">
                    {session.status === "closed" ? "Sesión de práctica completa" : "Sesión en progreso"} · {(session.rounds || []).length} rondas
                </p>
            </div>

            {session.status !== "closed" && (
                <div className="edu-card p-6 mb-4 text-center text-edu-muted text-sm">Esta sesión todavía no ha terminado — el reporte aparece cuando se complete la práctica.</div>
            )}

            {report && (
                <div className="edu-card p-6 mb-4">
                    <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest mb-2">Observaciones generales</h2>
                    <p className="text-sm text-edu-body whitespace-pre-wrap">{report.observaciones_generales}</p>
                </div>
            )}

            {report && (report.criterios || []).length > 0 && (
                <div className="edu-card p-6 mb-4">
                    <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest mb-3">Criterios de la rúbrica</h2>
                    <div className="space-y-3">
                        {report.criterios.map((c, idx) => {
                            const meta = NIVEL_META[c.nivel] || NIVEL_META.parcial;
                            const Icon = meta.icon;
                            return (
                                <div key={idx} className="border-t border-edu pt-3 first:border-t-0 first:pt-0">
                                    <p className={`text-sm font-medium inline-flex items-center gap-1.5 ${meta.style}`}>
                                        <Icon size={14} /> {c.criterio} — {meta.label}
                                    </p>
                                    <p className="text-sm text-edu-body mt-1">{c.comentario}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {grammar && (
                <div className="edu-card p-6 mb-4 border-l-4 border-l-violet-400">
                    <h2 className="text-sm font-semibold text-violet-800 mb-1 inline-flex items-center gap-1.5">
                        <SpellCheck size={14} /> Revisión de gramática (IA) — {grammar.error_count} error{grammar.error_count !== 1 ? "es" : ""}
                    </h2>
                    {grammar.summary && <p className="text-sm text-violet-900 mb-3">{grammar.summary}</p>}
                    {Object.keys(grammar.error_types || {}).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                            {Object.entries(grammar.error_types).map(([tipo, n]) => (
                                <span key={tipo} className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">{tipo}: {n}</span>
                            ))}
                        </div>
                    )}
                    <p className="text-[11px] text-edu-muted">
                        Apoyo de la IA, no una nota. Se basa en la transcripción automática del audio, así que no incluye ortografía ni puntuación. La calificación la decides tú.
                    </p>
                </div>
            )}

            <div className="edu-card p-6">
                <h2 className="text-sm font-semibold text-edu-muted uppercase tracking-widest mb-3 inline-flex items-center gap-1.5"><FileText size={14} /> Transcripción</h2>
                <div className="space-y-4">
                    {(session.rounds || []).map((r) => (
                        <div key={r.round_num} className="border-t border-edu pt-3 first:border-t-0 first:pt-0">
                            <p className="text-xs font-semibold text-edu-muted uppercase tracking-widest mb-1">Pregunta {r.round_num}</p>
                            <p className="text-sm text-edu-body mb-2">{r.pregunta}</p>
                            {r.transcripcion && (
                                <>
                                    <p className="text-xs font-semibold text-edu-muted uppercase tracking-widest mb-1">Respuesta</p>
                                    <p className="text-sm text-edu-body">{r.transcripcion}</p>
                                </>
                            )}
                            {(errorsByRound[r.round_num] || []).length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {errorsByRound[r.round_num].map((err, i) => (
                                        <div key={i} className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                                            <p className="text-sm text-violet-900 font-medium">"{err.excerpt}"</p>
                                            <p className="text-xs text-violet-500 uppercase tracking-wide mt-0.5">{err.type}</p>
                                            <p className="text-xs text-violet-800 mt-1"><span className="font-medium">Qué está mal:</span> {err.issue}</p>
                                            <p className="text-xs text-violet-700 mt-0.5"><span className="font-medium">Explicación:</span> {err.explanation}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
